import React, { useState, useMemo } from "react";
import { FaShieldAlt, FaCheck } from "react-icons/fa";
import useAuth from "../../../../Hooks/useAuth";
import { useAlert } from "../../../../context/AlertContext";
import {
  useGetDealerRolesRoleTemplatesQuery,
  useCreateDealerRolesRoleTemplateMutation,
  useUpdateDealerRolesRoleTemplateMutation,
  useDeleteDealerRolesRoleTemplateMutation,
} from "../../../../store";
import SettingKeysPicker from "../../../../Components/Firm/Tabs/SettingKeysPicker";
import { Drawer, Modal } from "../../../shared";
import { TextInput, Checkbox } from "../../../../Components/atoms";
import usePermission from "../../../../Hooks/usePermission";
import TemplateRail from "../TemplateRail";
import { ALL_TEMPLATES } from "../../../../utils/templateGrouping";

// Dealer'in permission seti — server'daki dealer-permissions.config.js ile eslesir
const DEALER_PERMISSIONS_LIST = [
  // Bayiler (alt bayi firmaları)
  { key: "dealers.read", description: "Bayi listesi görüntüleme (Bayilerim)" },
  { key: "dealers.info", description: "Bayi bilgi sekmesi" },
  { key: "dealers.editName", description: "Bayi adı değiştirme" },
  { key: "dealers.editDetails", description: "Bayi iletişim/vergi/not düzenleme" },
  { key: "dealers.editActivity", description: "Bayi aktiflik aç/kapat" },
  { key: "dealers.editAutoReport", description: "Bayi otomatik rapor ayarları" },
  { key: "dealers.devices", description: "Bayi cihazlar sekmesi" },
  { key: "dealers.stats", description: "Bayi performans sekmesi" },
  { key: "dealers.productSettings", description: "Bayi ürün ayarları sekmesi" },
  { key: "dealers.editProductSettings", description: "Bayi ürün ayarları düzenleme" },
  { key: "dealers.editProductQuotaDistribution", description: "Bayi ürün kota dağıtım ayarı" },
  { key: "dealers.editProductQuotaWarning", description: "Bayi ürün uyarı kotası" },
  { key: "dealers.belowFirms", description: "Alt bayiler sekmesi" },
  { key: "dealers.belowFirmsLink", description: "Alt bayi ekleme" },
  { key: "dealers.belowFirmsUnlink", description: "Alt bayi çıkarma" },
  { key: "dealers.customers", description: "Bayinin müşteri firmalari sekmesi" },
  { key: "dealers.users", description: "Bayi kullanıcıları sekmesi" },
  // Müşteriler (müşteri firmaları)
  { key: "customers.read", description: "Müşteri listesi görüntüleme (Müşterilerim)" },
  { key: "customers.info", description: "Müşteri bilgi sekmesi" },
  { key: "customers.editName", description: "Müşteri adı değiştirme" },
  { key: "customers.editDetails", description: "Müşteri iletişim/vergi/not düzenleme" },
  { key: "customers.editActivity", description: "Müşteri aktiflik aç/kapat" },
  { key: "customers.editAutoReport", description: "Müşteri otomatik rapor ayarları" },
  { key: "customers.devices", description: "Müşteri cihazlar sekmesi" },
  { key: "customers.stats", description: "Müşteri performans sekmesi" },
  { key: "customers.productSettings", description: "Müşteri ürün ayarları sekmesi" },
  { key: "customers.editProductSettings", description: "Müşteri ürün ayarları düzenleme" },
  { key: "customers.editProductQuotaDistribution", description: "Müşteri ürün kota dağıtım ayarı" },
  { key: "customers.editProductQuotaWarning", description: "Müşteri ürün uyarı kotası" },
  { key: "customers.sendQuota", description: "Müşteriye kota verme / müşteriden kota alma" },
  { key: "customers.users", description: "Müşteri kullanıcıları sekmesi" },
  { key: "customers.employees", description: "Müşteri çalışanları sekmesi" },
  { key: "customers.employeesLink", description: "Müşteri çalışanı ekleme" },
  { key: "customers.employeesUnlink", description: "Müşteri çalışanı çıkarma" },
  { key: "customers.employeesEditQuota", description: "Müşteri çalışan kota düzenleme" },
  // Cihazlar (Cihazlarim sayfasi + cihaz detayi)
  { key: "devices.read", description: "Cihaz listesi görüntüleme" },
  { key: "devices.info", description: "Cihaz bilgi sekmesi" },
  { key: "devices.infoFirm", description: "Cihaz bilgi: firma bilgileri kartı" },
  { key: "devices.infoNote", description: "Cihaz bilgi: notlar kartı" },
  { key: "devices.infoProducts", description: "Cihaz bilgi: ürün bilgileri kartları" },
  { key: "devices.stats", description: "Cihaz istatistik sekmesi" },
  { key: "devices.reports", description: "Cihaz raporlar sekmesi" },
  { key: "devices.reportConsumption", description: "Cihaz tüketim logları" },
  { key: "devices.reportOperation", description: "Cihaz işlem logları" },
  { key: "devices.reportSettings", description: "Cihaz ayar değişiklik logları" },
  // Versiyon/GSM kartı, hareket, ayarlar (+ bakım) ve bilgi değişiklik logları
  // bayide YOK (#302); ürün bilgisi salt okunur — editInfo yalnız not.
  { key: "devices.editInfo", description: "Cihaz bilgi düzenleme (not)" },
  { key: "devices.toggleActivity", description: "Cihaz kota almaya aç/kapa" },
  { key: "devices.moveToFirm", description: "Cihazı firmaya ver" },
  { key: "devices.moveToService", description: "Cihazı servise al" },
  { key: "devices.moveToPlaymaker", description: "Cihazı plasiyere ver" },
  { key: "devices.moveToStorage", description: "Cihazı depoya al" },
  { key: "devices.quotaBalance", description: "Cihazlar sekmesinde firma kota bakiyesi" },
  { key: "devices.giveQuota", description: "Cihaza kota verme (firma bakiyesinden)" },
  { key: "devices.takeBackQuota", description: "Cihazdan kota geri alma (firma bakiyesine)" },
  // Dashboard
  { key: "dashboard.read", description: "Dashboard tüketim kartları" },
  { key: "dashboard.graph", description: "Tüketim grafiği" },
  { key: "dashboard.scope", description: "Kapsam seçici" },
  { key: "dashboard.quota", description: "Bayi kotası kartı" },
  // NOT: Fatura, Rapor ve Rol Şablonu yetkileri şimdilik kapsam dışı.
];

const PERMISSION_CATEGORIES = {
  dealers: "Bayiler",
  customers: "Müşteriler",
  devices: "Cihazlar",
  bills: "Faturalar",
  dashboard: "Dashboard",
  firms: "Rol Şablonları",
};

const groupPermissions = (permissions) => {
  const groups = {};
  permissions.forEach((p) => {
    const cat = p.key.split(".")[0];
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(p);
  });
  return groups;
};

// Kategori gösterim sırası — üstte Dashboard, sonra Cihazlar, sonra Bayiler/Müşteriler.
const CATEGORY_ORDER = ["dashboard", "devices", "dealers", "customers", "bills", "firms"];
const orderCategories = (entries) =>
  [...entries].sort(([a], [b]) => {
    const ia = CATEGORY_ORDER.indexOf(a);
    const ib = CATEGORY_ORDER.indexOf(b);
    return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
  });


const PERMISSION_LABELS = {
  // Firma
  "firms.read": "Firma Listesi Görüntüleme",
  "firms.info": "Firma Bilgi Sekmesi",
  "firms.editName": "Firma Adı Değiştirme",
  "firms.editDetails": "Firma Detay Düzenleme",
  "firms.editActivity": "Firma Aktiflik Aç/Kapat",
  "firms.editAutoReport": "Firma Otomatik Rapor Ayarları Düzenleme",
  "firms.devices": "Firma Cihazlar Sekmesi",
  "firms.stats": "Firma Performans Sekmesi",
  "firms.bills": "Firma Faturalar Sekmesi",
  "firms.productSettings": "Firma Ürün Ayarları",
  "firms.belowFirms": "Alt Firmalar Sekmesi",
  "firms.belowFirmsLink": "Alt Firma Ekleme",
  "firms.belowFirmsUnlink": "Alt Firma Çıkarma",
  "firms.users": "Firma Kullanıcıları Sekmesi",
  "firms.customers": "Müşteri Firmaları Sekmesi",
  "firms.employees": "Çalışanlar Sekmesi",
  "firms.employeesLink": "Çalışan Ekleme",
  "firms.employeesUnlink": "Çalışan Çıkarma",
  "firms.employeesEditQuota": "Çalışan Kota Düzenleme",
  "firms.logConsumption": "Firma Tüketim Logları",
  "firms.logInfo": "Firma Bilgi Değişikliği Logları",
  "firms.logBill": "Firma Fatura Hareketi Logları",
  "firms.logSettings": "Firma Ayar Değişikliği Logları",
  "firms.logOperation": "Firma Operasyon Logları",
  "firms.logQuota": "Firma Kota Değişikliği Logları",
  "firms.roleTemplatesRead": "Kullanıcı Şablonları Görüntüleme",
  "firms.roleTemplatesCreate": "Kullanıcı Şablonu Oluşturma",
  "firms.roleTemplatesEdit": "Kullanıcı Şablonu Düzenleme",
  "firms.roleTemplatesDelete": "Kullanıcı Şablonu Silme",
  "firms.editProductSettings": "Ürün Ayarları Düzenleme",
  "firms.editProductQuotaDistribution": "Ürün Kota Dağıtım Ayarı",
  "firms.editProductQuotaWarning": "Ürün Uyarı Kotası",
  // Cihaz
  "devices.quotaBalance": "Cihazlarda Kota Bakiyesi",
  "devices.giveQuota": "Cihaza Kota Verme",
  "devices.takeBackQuota": "Cihazdan Kota Alma",
  "devices.read": "Cihaz Listesi Görüntüleme",
  "devices.info": "Cihaz Bilgi Sekmesi",
  "devices.infoFirm": "Firma Bilgileri Kartı",
  "devices.infoNote": "Notlar Kartı",
  "devices.infoProducts": "Ürün Bilgileri Kartları",
  "devices.stats": "Cihaz İstatistik Sekmesi",
  "devices.reports": "Cihaz Raporlar Sekmesi",
  "devices.reportConsumption": "Cihaz Tüketim Logları",
  "devices.reportOperation": "Cihaz İşlem Logları",
  "devices.reportSettings": "Cihaz Ayar Değişiklik Logları",
  // Cihaz — Operasyonlar
  "devices.toggleActivity": "Cihaz Kota Almaya Aç/Kapa",
  "devices.moveToFirm": "Cihazı Firmaya Ver",
  "devices.moveToService": "Cihazı Servise Al",
  "devices.moveToPlaymaker": "Cihazı Plasiyere Ver",
  "devices.moveToStorage": "Cihazı Depoya Al",
  // Fatura
  "bills.read": "Faturaları Görüntüleme",
  "bills.create": "Fatura Oluşturma",
  "bills.controll": "Fatura Kontrol",
  // Dashboard
  "dashboard.read": "Dashboard Tüketim Kartları",
  "dashboard.graph": "Tüketim Grafiği",
  "dashboard.scope": "Kapsam Seçici",
  "dashboard.quota": "Bayi Kotası Kartı",
};

// Izin katalogu bu dosyada sabit oldugu icin fallback de yerelden gelir:
// once kisa Turkce ad, yoksa katalogdaki aciklama, son care ham key.
// (storageRoles/RoleTemplatesTab.js'te ayni fallback sunucudan besleniyor.)
const PERMISSION_DESCRIPTIONS = DEALER_PERMISSIONS_LIST.reduce((acc, p) => {
  acc[p.key] = p.description;
  return acc;
}, {});

const getPermissionLabel = (key) =>
  PERMISSION_LABELS[key] || PERMISSION_DESCRIPTIONS[key] || key;


// ─── Template Form Drawer ────────────────────────────────
function TemplateFormDrawer({ open, onClose, firmId, editTemplate, token }) {
  const { showSuccess, showError } = useAlert();
  const [createTemplate, { isLoading: creating }] = useCreateDealerRolesRoleTemplateMutation();
  const [updateTemplate, { isLoading: updating }] = useUpdateDealerRolesRoleTemplateMutation();

  const grouped = useMemo(() => groupPermissions(DEALER_PERMISSIONS_LIST), []);

  const [name, setName] = useState(editTemplate?.name || "");
  const [selected, setSelected] = useState(new Set(editTemplate?.permissions || []));
  // Yetenek bayragi — bkz. storageRoles/RoleTemplatesTab.js
  // Tam yetki — izin listesi kontrolu atlanir. Sunucu bu bayragi yalnizca
  // admin veya zaten tam yetkili kullanicidan kabul eder (403 aksi halde).
  // "Tam yetkili" bayi sablonlarindan KALDIRILDI (kullanici karari 2026-08-13):
  // sunucuda zaten wildcard vermiyordu (requirePermission bypass'i
  // role === "storage" sartina bagli), yalnizca log izin kontrolunu atlatiyordu.
  // Etiket "her islemi yapabilir" dedigi icin yanilticiydi.
  const [canTakeDevice, setCanTakeDevice] = useState(editTemplate?.canTakeDevice === true);
  // Ayar YAZMA anahtarlari — bos = hicbir ayar degistirilemez.
  const [settingWriteKeys, setSettingWriteKeys] = useState(
    editTemplate?.settingWriteKeys || [],
  );
  const [settingReadKeys, setSettingReadKeys] = useState(
    editTemplate?.settingReadKeys || [],
  );
  const [activeTab, setActiveTab] = useState("permissions");

  React.useEffect(() => {
    setName(editTemplate?.name || "");
    setSelected(new Set(editTemplate?.permissions || []));
    setCanTakeDevice(editTemplate?.canTakeDevice === true);
    setSettingWriteKeys(editTemplate?.settingWriteKeys || []);
    setSettingReadKeys(editTemplate?.settingReadKeys || []);
  }, [editTemplate]);

  const togglePermission = (key) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleCategory = (catPermissions) => {
    const allSelected = catPermissions.every((p) => selected.has(p.key));
    setSelected((prev) => {
      const next = new Set(prev);
      catPermissions.forEach((p) => {
        if (allSelected) next.delete(p.key);
        else next.add(p.key);
      });
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      showError("Şablon adı zorunludur!");
      return;
    }
    // Kural: sablon adi = subRole (server da name'i kaynak alir)
    const payload = {
      token,
      firmId,
      name: name.trim(),
      subRole: name.trim(),
      permissions: [...selected],
      canTakeDevice,
      settingWriteKeys,
      settingReadKeys,
    };
    try {
      if (editTemplate) {
        await updateTemplate({ ...payload, templateId: editTemplate._id }).unwrap();
        showSuccess("Rol şablonu güncellendi.");
      } else {
        await createTemplate(payload).unwrap();
        showSuccess("Rol şablonu oluşturuldu.");
      }
      onClose();
    } catch (err) {
      showError(err?.data?.message || "İşlem başarısız oldu.");
    }
  };

  const isSubmitting = creating || updating;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editTemplate ? "Şablonu Düzenle" : "Yeni Rol Şablonu"}
      subtitle="Rol şablonu ayarları ve yetkileri"
      titleIcon={<FaShieldAlt className="w-5 h-5 text-orange-500" />}
      width="lg"
    >
      <Drawer.Body className="p-5 space-y-6">
        {/* Sablon adi ayni zamanda alt rol (subRole) olarak kullanilir */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Şablon Adı (Alt Rol)</label>
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="örn: Depo" disabled={!!editTemplate} />
            <p className="text-xs text-gray-400 mt-1">Şablon adı, kullanıcıların alt rolü (subRole) olarak da kullanılır.</p>
          </div>

          {/* Yetenek — yetki listesiyle ifade edilemeyen davranis */}
          <label className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50/50 p-3.5 cursor-pointer">
            <input
              type="checkbox"
              checked={canTakeDevice}
              onChange={(e) => setCanTakeDevice(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-orange-500"
            />
            <span>
              <span className="block text-sm font-Medium text-gray-700">Cihaz atanabilir (plasiyer)</span>
              <span className="block text-xs text-gray-500 mt-0.5">
                İşaretlenirse bu şablondaki kullanıcılar "Plasiyere Ver" listesinde
                görünür. Şablon adından bağımsızdır.
              </span>
            </span>
          </label>
        </div>
        <div className="flex items-center gap-1 border-b border-gray-200">
          {[
            { id: "permissions", label: "Yetkiler" },
            { id: "settings", label: "Ayarlar" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActiveTab(t.id)}
              className={`px-3 py-2 text-sm font-SemiBold border-b-2 -mb-px transition-colors ${
                activeTab === t.id
                  ? "border-main text-gray-900"
                  : "border-transparent text-gray-400 hover:text-gray-600"
              }`}
            >
              {t.label}
              {t.id === "settings" && settingReadKeys.length > 0 && (
                <span className="ml-1.5 text-[11px] text-indigo-600">
                  {settingReadKeys.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === "settings" && (
          <SettingKeysPicker
            readKeys={settingReadKeys}
            writeKeys={settingWriteKeys}
            onChange={({ readKeys, writeKeys }) => {
              setSettingReadKeys(readKeys);
              setSettingWriteKeys(writeKeys);
            }}
          />
        )}

        <div className={activeTab === "permissions" ? "" : "hidden"}>
          <h3 className="text-sm font-semibold text-gray-800 mb-3">Yetkiler</h3>
          <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-1">
            {orderCategories(Object.entries(grouped)).map(([cat, perms]) => {
              const allChecked = perms.every((p) => selected.has(p.key));
              const someChecked = perms.some((p) => selected.has(p.key));
              return (
                <div key={cat} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-2 cursor-pointer" onClick={() => toggleCategory(perms)}>
                    <div className={`w-4 h-4 rounded border flex items-center justify-center text-xs ${allChecked ? "bg-main border-main text-white" : someChecked ? "bg-primary/30 border-primary" : "border-gray-300"}`}>
                      {allChecked && <FaCheck className="w-2.5 h-2.5" />}
                    </div>
                    <span className="font-medium text-sm text-gray-800">{PERMISSION_CATEGORIES[cat] || cat}</span>
                    <span className="text-xs text-gray-400">({perms.filter((p) => selected.has(p.key)).length}/{perms.length})</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 ml-6">
                    {perms.map((p) => (
                      <label key={p.key} className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer hover:text-gray-900">
                        <Checkbox checked={selected.has(p.key)} onChange={() => togglePermission(p.key)} />
                        <span>{p.description}</span>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Drawer.Body>
      <Drawer.Footer className="p-4 border-t border-gray-200">
        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">İptal</button>
          <button onClick={handleSubmit} disabled={isSubmitting} className="px-4 py-2 text-sm text-white bg-main rounded-lg hover:bg-mainSecond disabled:opacity-50">
            {isSubmitting ? "Kaydediliyor..." : editTemplate ? "Güncelle" : "Oluştur"}
          </button>
        </div>
      </Drawer.Footer>
    </Drawer>
  );
}

// ─── Ana Tab ─────────────────────────────────────────────
/**
 * Kullanıcı şablonları — Kullanıcılar sekmesinin solundaki şablon sütunu
 * (#298; eskiden ayrı sekmede kart ızgarasıydı). Seçim ve kişi sayıları
 * çağırandan (dealerRoles/UsersTab) gelir; çekmece + silme onayı burada.
 */
export default function RoleTemplatesTab({ firm, selectedId = ALL_TEMPLATES, onSelect, stats }) {
  const { auth } = useAuth();
  const { showSuccess, showError } = useAlert();
  const { hasPermission } = usePermission();

  const { data, isLoading } = useGetDealerRolesRoleTemplatesQuery(
    { token: auth.accessToken, id: firm?._id },
    { skip: !firm?._id }
  );

  const [deleteTemplate, { isLoading: deleting }] = useDeleteDealerRolesRoleTemplateMutation();

  const templates = data?.data?.templates || [];
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editTemplate, setEditTemplate] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const canCreate = hasPermission("firms.roleTemplatesCreate");
  const canEdit = hasPermission("firms.roleTemplatesEdit");
  const canDelete = hasPermission("firms.roleTemplatesDelete");

  const handleEdit = (t) => { setEditTemplate(t); setDrawerOpen(true); };
  const handleCreate = () => { setEditTemplate(null); setDrawerOpen(true); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteTemplate({ token: auth.accessToken, firmId: firm._id, templateId: deleteTarget._id }).unwrap();
      showSuccess("Rol şablonu silindi.");
      if (String(selectedId) === String(deleteTarget._id)) onSelect?.(ALL_TEMPLATES);
      setDeleteTarget(null);
    } catch (err) {
      showError(err?.data?.message || "Silme işlemi başarısız oldu.");
    }
  };

  // Seçili şablonun özeti: yetki sayısı + ilk üç yetki.
  const renderSummary = (t) => {
    const perms = t.permissions || [];
    return (
      <div className="space-y-1.5">
        <p className="text-[11px] text-gray-500">{perms.length} yetki</p>
        {perms.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {perms.slice(0, 3).map((perm) => (
              <span key={perm} className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-600 rounded" title={perm}>{getPermissionLabel(perm)}</span>
            ))}
            {perms.length > 3 && <span className="px-1.5 py-0.5 text-[10px] text-gray-400">+{perms.length - 3}</span>}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <TemplateRail
        title="Kullanıcı Şablonları"
        accent="indigo"
        templates={templates}
        stats={stats}
        selectedId={selectedId}
        onSelect={onSelect}
        loading={isLoading}
        canCreate={canCreate}
        onCreate={handleCreate}
        canEdit={canEdit}
        onEdit={handleEdit}
        // Varsayilan (sistem) sablonlar silinemez — sunucu da 400 doner.
        canDeleteTemplate={(t) => canDelete && t.isSystem !== true}
        onDelete={setDeleteTarget}
        renderSummary={renderSummary}
      />

      <TemplateFormDrawer open={drawerOpen} onClose={() => { setDrawerOpen(false); setEditTemplate(null); }} firmId={firm?._id} editTemplate={editTemplate} token={auth.accessToken} />

      <Modal open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <Modal.Header title="Şablonu Sil" onClose={() => setDeleteTarget(null)} />
        <Modal.Body><p className="text-sm text-gray-600"><strong>{deleteTarget?.name}</strong> rol şablonunu silmek istediğinize emin misiniz?</p></Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3">
            <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">İptal</button>
            <button onClick={handleDelete} disabled={deleting} className="px-4 py-2 text-sm text-white bg-fifth rounded-lg hover:bg-red-600 disabled:opacity-50">{deleting ? "Siliniyor..." : "Sil"}</button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  );
}
